import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import type { Language, Translations, I18nContextType } from './types'
import enLocale from './locales/en.json'
import esLocale from './locales/es.json'
import ruLocale from './locales/ru.json'

const flattenLocale = (locale: Record<string, unknown>, prefix = ''): Translations => {
  return Object.entries(locale).reduce<Translations>((acc, [key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(acc, flattenLocale(value as Record<string, unknown>, nextKey))
    } else if (typeof value === 'string') {
      acc[nextKey] = value
    } else if (value !== undefined && value !== null) {
      acc[nextKey] = String(value)
    }
    return acc
  }, {})
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

const translations: Record<string, Translations> = {
    en: {
      'common.sortBy': 'Sort by:',
      'common.filterBySeverity': 'Filter by severity:',
    // App
    'app.name': 'DefendSphere',
    // A11y
    'a11y.languageSwitcher': 'Language switcher',
    'a11y.languageSelect': 'Select language',
    'a11y.chatInput': 'Type your message',
    'a11y.chatSend': 'Send message',
    // Navigation
    'nav.home': 'Home',
    'nav.assets': 'Assets',
    'nav.compliance': 'Compliance',
    'nav.customerTrust': 'Security Governance & Documentation',
    'nav.suppliers': 'Suppliers',
    'nav.reports': 'Reports',
    'nav.starterGuide': 'Starter Guide',
    'nav.helpAsk': 'Help ask',
    'nav.integrations': 'Integrations',
    'integrations.aiLead':
      'Optional shortcuts for LLM keys used by the assistant. Saving keys and choosing the active provider are admin actions in the main Integrations / AI Assistant area; this page does not activate a provider.',
    'integrations.ai.manageAccess': 'Manage access',
    'integrations.ai.applyAccessChanges': 'Apply changes',
    'integrations.ai.searchOrganizations': 'Search organizations…',
    'integrations.ai.selectAll': 'Select all',
    'integrations.ai.accessGrantedCount': 'Organizations with access',
    'integrations.ai.accessPartialFailure':
      'Some access changes failed. The window stays open so you can retry.',
    'integrations.ai.noOrganizations': 'No organizations found',
    'integrations.ai.noSearchMatches': 'No organizations match your search',
    'integrations.ai.orgsLoadFailed': 'Failed to load organizations',
    'integrations.ai.noPlatformProviders': 'No platform providers configured yet.',
    'integrations.ai.accessGranted': 'Access granted',
    'integrations.ai.unknownOrganization': 'Organization',
    'integrations.ai.disabled': 'Disabled',
    'nav.userDashboard': 'User Settings',
    'nav.notifications': 'Notifications',
    // incidents/alerts removed
    'nav.settings': 'Settings',
    'nav.admin': 'Admin Panel',
    
    // Auth
    'auth.login': 'Sign In',
    'auth.register': 'Sign Up',
    'auth.logout': 'Logout',
    'auth.invalidCredentials': 'Invalid username or password. Please try again.',
    
    // Admin Panel
    'admin.error.fetchUsers': 'Failed to fetch users',
    'admin.error.createUser': 'Failed to create user',
    'admin.error.updateUser': 'Failed to update user',
    'admin.error.generic': 'An error occurred',
    'admin.placeholder.newOrgName': 'New organization name',
    'admin.placeholder.filterByOrg': 'Filter by organization...',
    'admin.placeholder.filterByOrgId': 'Filter by organization (ID)',
    'admin.placeholder.orgList': 'Org A, Org B',
    'admin.placeholder.ipAddress': '217.65.144.232',
    'admin.placeholder.port': '9392',
    'admin.placeholder.username': 'admin',
    'admin.placeholder.password': '••••••••',
    
    // Assets
    'assets.error.fetchFailed': 'Failed to fetch assets data',
    
    // Reports
    'reports.error.fetchFailed': 'Failed to fetch report data',
    'reports.headerTitle': 'Compliance Reports',
    // Sections
    'suppliers.title': 'Suppliers',
    'suppliers.open': 'Open Suppliers',
    'customerTrust.title': 'Security Governance & Documentation',
    'customerTrust.open': 'Open Governance',
    'compliance.progress': 'Compliance Progress',
    'compliance.open': 'Open Compliance',
    'reports.error.organizationNotFound': 'Organization not found in session',
    'reports.error.uploadFailed': 'Upload failed',
    'reports.pageSubtitle': 'View and export your compliance reports',
    'reports.backToDashboard': 'Back to Dashboard',
    'reports.recentTitle': 'Recent Reports',
    'reports.recentSubtitle': 'View and export your compliance reports',
    'reports.table.reportName': 'Report name',
    'reports.table.date': 'Date',
    'reports.table.status': 'Status',
    'reports.table.actions': 'Actions',
    'reports.action.view': 'View',
    'reports.action.exportPdf': 'Export PDF',
    
    // Starter Guide
    'starterGuide.options.yes': 'Yes',
    'starterGuide.options.no': 'No',
    'starterGuide.options.inDevelopment': 'In development',
    'starterGuide.options.externalProvider': 'External provider',
    'starterGuide.options.notSure': 'Not sure',
    'starterGuide.options.sometimes': 'Sometimes',
    'starterGuide.sectors.banking': 'Banking',
    'starterGuide.sectors.chemical': 'Chemical industry',
    'starterGuide.sectors.digitalInfrastructure': 'Digital infrastructure',
    'starterGuide.sectors.digitalServiceProviders': 'Digital service providers',
    'starterGuide.sectors.healthcare': 'Healthcare',
    'starterGuide.sectors.transport': 'Transport',
    'starterGuide.sectors.other': 'Other',
    'starterGuide.locations.onPremise': 'On-premise',
    'starterGuide.locations.rented': 'Rented',
    'starterGuide.locations.cloud': 'Cloud',
    'starterGuide.locations.notSure': 'Not sure',
    'starterGuide.dataTypes.name': 'Name',
    'starterGuide.dataTypes.email': 'Email',
    'starterGuide.dataTypes.phone': 'Phone',
    'starterGuide.dataTypes.financial': 'Financial data',
    'starterGuide.dataTypes.address': 'Address',
    
    // Error Pages
    'error.somethingWentWrong': 'Something went wrong',
    'error.unexpectedError': 'We\'re sorry, but something unexpected happened. Please try refreshing the page.',
    'error.details': 'Error Details (Development)',
    'error.componentStack': 'Component Stack:',
    'error.tryAgain': 'Try Again',
    'error.refreshPage': 'Refresh Page',
    
    'auth.username': 'Username',
    'auth.password': 'Password',
    'auth.email': 'Email',
    'auth.welcome': 'Welcome',
    'auth.signInToDashboard': 'Sign in to your security dashboard',
    'auth.defaultUsers': 'Default users:',
    'auth.signingIn': 'Signing in...',
    'auth.connectionError': 'Connection error. Please try again.',
    'auth.loginFailed': 'Login failed',
    
    // Dashboard
    'dashboard.title': 'Security Dashboard',
    'dashboard.incidentsMonitoring': 'Incidents Monitoring',
    'dashboard.openReports': 'Open Reports',
    'dashboard.assets': 'Assets',
    'dashboard.incidents': 'Incidents',
    'dashboard.criticalIncidents': 'Critical Incidents',
      'dashboard.noData': 'No data',
    // Scan Request
    'scan.request.title': 'Launch asset scan request',
    'scan.start_scan': 'Start Scan',
    'scan.scanner_type': 'Scanner Type',
    'scan.openvas_full': 'OpenVAS (network/infrastructure scanner)',
    'scan.active_scans': 'Active Scans',
    'scan.no_active_scans': 'No active scans',
    'scan.cve_scanner': 'CVE Scanner (NVD / SCAP / OVAL)',
    'scan.update_status': 'Update Status',
    'scan.download_report': 'Download report',
    'scan.details': 'Details',
    'scan.col.taskId': 'Task ID',
    'scan.col.name': 'Name',
    'scan.col.target': 'Target',
    'scan.col.status': 'Status',
    'scan.col.progress': 'Progress',
    'scan.col.startedAt': 'Started',
    'scan.col.reportId': 'Report ID',
    'scan.col.actions': 'Actions',
    'scan.col.config': 'Config',
    'scan.col.scanner': 'Scanner',
    'scan.request.assetsAvailable': '{count} assets available for scan',
    'scan.request.yourAssets': 'Your assets:',
    'scan.request.manualInput': 'Manual input (DNS/IP + port)',
    'scan.request.exampleHost': 'example.com or 1.2.3.4',
    'scan.request.port': 'port',
    'scan.request.addResource': '+ Add resource',
    'scan.request.submit': 'Submit scan request',
    'scan.starting': 'Starting scan...',
    'scan.started': 'Scan started successfully',
    'scan.failed': 'Failed to start scan',
    'scan.assetType.webServer': 'Web Server',
    'scan.environment.production': 'Production',

    // Help Request
    'help.request.title': 'Submit Request / Ask for Help',
    'help.request.placeholder': 'Describe the issue or request...',
    'help.request.submit': 'Submit request',
    'help.request.noFile': 'No file chosen',
    'help.request.submitButton': 'Submit Request',
    'compliance.fillIn': 'Fill in',
    'compliance.survey.title': 'Survey: {standard}',
    'compliance.survey.hide': 'Hide',
    'compliance.survey.save': 'Save',
    'compliance.survey.cancel': 'Cancel',
    'compliance.survey.loading': 'Loading survey...',
    'compliance.survey.ratings': 'Ratings: 1 — not implemented; 2 — partially; 3 — average level; 4 — good level; 5 — full compliance.',
    'compliance.survey.lastSavedLabel': 'Last saved',
    'compliance.survey.byUser': '(by {user})',
    'compliance.survey.currentScoreLabel': 'Current Score',
    'assets.organizationTitle': 'Organization Assets: {orgName}',
      'reports.title': 'Organization Reports: {orgName}',
      'reports.organizationTitle': 'Organization: {orgName}',
    'reports.summary': 'Summary',
    'reports.dragDrop': 'Drag & Drop PDF here',
    'reports.uploadPdf': 'Upload PDF',
    'reports.chooseFile': 'Choose File',
    'reports.uploadCta': 'Upload Vulnerability Report (CSV/PDF/Excel)',

    // Assistant
    'assistant.title': 'Security Assistant',
    'assistant.greeting': "👋 Hello! I'm your Security Assistant powered by Gemini AI. How can I help you today?",
    'assistant.unavailable': 'Assistant is unavailable. Please try again later.',
    'assistant.integrationHint': 'AI Assistant is not available. Configure integration in Admin Panel → Integrations.',
    'assistant.open': 'Open chat',
    'assistant.close': 'Close chat',
    'assistant.placeholder': 'Type your message…',
    'assistant.send': 'Send',
    'assistant.gemini.configured': 'Gemini AI Assistant is configured and ready',
    'assistant.gemini.testing': 'Testing Gemini connection...',
    'assistant.typing': 'Assistant is typing...',
    // GDPR Survey Sections
    'gdpr.section1.title': '1. Legal basis and lawfulness of data processing',
    'gdpr.section1.q1': 'Are the grounds for processing personal data defined?',
    'gdpr.section1.q2': 'Is a privacy policy formed and user consent obtained?',
    'gdpr.section1.q3': 'Can data subjects refuse to have their data processed?',
    'gdpr.section2.title': '2. Clarity and transparency in user interaction',
    'gdpr.section2.q1': 'Is information provided about the purposes and methods of data processing?',
    'gdpr.section2.q2': 'Is notification about data subject rights always available?',
    'gdpr.section2.q3': 'Is it easy to familiarize with the data processing procedure?',
    'gdpr.section3.title': '3. Limitation of data processing purposes',
    'gdpr.section3.q1': 'Do you limit data collection to only necessary categories?',
    'gdpr.section3.q2': 'Do you set data deletion deadlines?',
    'gdpr.section4.title': '4. Data quality and accuracy',
    'gdpr.section4.q1': 'Do you check data relevance and correctness?',
    'gdpr.section4.q2': 'Do you monitor changes and errors?',
    'gdpr.section5.title': '5. Data storage and transmission security',
    'gdpr.section5.q1': 'Are modern protection measures applied (encryption, access restrictions)?',
    'gdpr.section5.q2': 'Is a system for preventing and detecting leaks established?',
    'gdpr.section6.title': '6. Responsibility and accountability',
    'gdpr.section6.q1': 'Is a Data Protection Officer (DPO) appointed?',
    'gdpr.section6.q2': 'Is documentation on data processing stored?',
    'gdpr.section6.q3': 'Are regular GDPR compliance reviews conducted?',
    'gdpr.section7.title': '7. Cross-border data transfer',
    'gdpr.section7.q1': 'Are guarantees used when transferring data abroad?',
    'gdpr.section7.q2': 'Are there confidentiality agreements with foreign partners?',
    'gdpr.section8.title': '8. Incident management and response',
    'gdpr.section8.q1': 'Is a system for detecting and responding to incidents developed?',
    'gdpr.section8.q2': 'Do you report violations to supervisory authorities and stakeholders?',
    'gdpr.section9.title': '9. Employee training and awareness',
    'gdpr.section9.q1': 'Is employee training on GDPR issues organized?',
    'gdpr.section9.q2': 'Are internal trainings conducted?',
    // DORA Survey Sections
    'dora.section1.title': '1. Digital risk management',
    'dora.section1.q1': 'Is a digital risk management mechanism created?',
    'dora.section1.q2': 'Are specific roles and authorities assigned to personnel?',
    'dora.section1.q3': 'Is the threat tracking and elimination system effective?',
    'dora.section2.title': '2. Level of technological equipment',
    'dora.section2.q1': 'Does the infrastructure meet modern reliability requirements?',
    'dora.section2.q2': 'Is monitoring and analysis of technological risks ensured?',
    'dora.section2.q3': 'Are the technologies used modern?',
    'dora.section3.title': '3. Personnel potential and qualifications',
    'dora.section3.q1': 'Are employees trained in digital risk management methods?',
    'dora.section3.q2': 'Are resources allocated for professional development?',
    'dora.section3.q3': 'Is there experience in participating in training exercises?',
    'dora.section4.title': '4. Internal resilience culture',
    'dora.section4.q1': 'Does management demonstrate personal commitment to resilience culture?',
    'dora.section4.q2': 'Is the importance of resilience perceived by employees?',
    'dora.section4.q3': 'Is the value of resilience recognized by senior management?',
    'dora.section5.title': '5. External relationships and partnerships',
    'dora.section5.q1': 'Does the organization collaborate with partners on resilience issues?',
    'dora.section5.q2': 'Are partner practices compatible with the organization\'s approaches?',
    'dora.section5.q3': 'Does participation in professional communities influence strategy?',
    'dora.section6.title': '6. Risk transmission and responsibility',
    'dora.section6.q1': 'Are rules for risk and responsibility transfer organized?',
    'dora.section6.q2': 'Are structures capable of responding quickly to emerging risks?',
    'dora.section6.q3': 'Is the organization ready for increased loads in crisis?',
    'dora.section7.title': '7. Innovative development',
    'dora.section7.q1': 'Does the organization invest in new technologies and services?',
    'dora.section7.q2': 'Does it help partners improve their resilience?',
    'dora.section7.q3': 'Does it follow the latest trends and technologies?',
    'dora.section8.title': '8. Reporting and communications',
    'dora.section8.q1': 'Is accessible reporting on digital resilience organized?',
    'dora.section8.q2': 'Is communication transparency maintained?',
    'dora.section8.q3': 'Is an incident and failure log maintained?',
    'dashboard.threatsOverTime': 'Threats Over Time',
    'dashboard.threatTypes': 'Threat Types',
    'dashboard.recentIncidents': 'Recent Incidents',
    'dashboard.time': 'Time',
    'dashboard.type': 'Type',
    'dashboard.severity': 'Severity',
    'dashboard.status': 'Status',
    'dashboard.high': 'High',
    'dashboard.medium': 'Medium',
    'dashboard.low': 'Low',
    
    // Admin Panel
    'admin.title': 'Admin Panel',
    'admin.users': 'Users',
    'admin.addUser': 'Add User',
    'admin.editUser': 'Edit User',
    'admin.deleteUser': 'Delete User',
    'admin.userManagement': 'User Management',
    'admin.role': 'Role',
    'admin.permissions': 'Permissions',
    'admin.createdAt': 'Created At',
    'admin.lastLogin': 'Last Login',
    'admin.actions': 'Actions',
    'admin.admin': 'Admin',
    'admin.user': 'User',
    'admin.save': 'Save',
    'admin.cancel': 'Cancel',
    'admin.delete': 'Delete',
    'admin.confirmDelete': 'Are you sure you want to delete this user?',
    'admin.userCreated': 'User created successfully',
    'admin.userUpdated': 'User updated successfully',
    'admin.userDeleted': 'User deleted successfully',
    'admin.error': 'An error occurred',
    'admin.attachReportPdf': 'Attach report (PDF)',
    'admin.uploadReport': 'Upload report',
    'admin.uploadReportFailed': 'Failed to upload report',
    'admin.orgDeletion.title': 'Schedule organization deletion',
    'admin.orgDeletion.warnTitle': 'This action schedules permanent deletion',
    'admin.orgDeletion.warnBody': 'The organization will be retained for 5 days, then permanently removed. Users and assets linked to this organization may lose access.',
    'admin.orgDeletion.organizationLabel': 'Organization',
    'admin.orgDeletion.confirmNameLabel': 'Type organization name to confirm',
    'admin.orgDeletion.confirmNameHint': 'Enter the name exactly as shown above (case and spaces matter).',
    'admin.orgDeletion.nameMismatchInline': 'Name does not match. Expected: {{name}}',
    'admin.orgDeletion.nameMismatch': 'The name you entered does not match the organization name. Copy it exactly, including spaces and capitalization.',
    'admin.orgDeletion.commentLabel': 'Reason for deletion (audit log)',
    'admin.orgDeletion.commentPlaceholder': 'Describe why this organization should be removed…',
    'admin.orgDeletion.commentHint': 'This comment is mandatory and will be stored in the audit trail.',
    'admin.orgDeletion.commentTooShortInline': 'Minimum 5 characters required.',
    'admin.orgDeletion.commentTooShort': 'Please provide a reason of at least 5 characters. This will be stored in the audit log.',
    'admin.orgDeletion.alreadyScheduled': 'Deletion is already scheduled for this organization.',
    'admin.orgDeletion.notFound': 'Organization was not found. Refresh the page and try again.',
    'admin.orgDeletion.genericError': 'Failed to schedule organization deletion.',
    'admin.orgDeletion.submit': 'Schedule deletion',
    'admin.orgDeletion.submitting': 'Scheduling…',
    'admin.orgDeletion.success': 'Deletion scheduled for {{name}}.',
    'admin.orgDeletion.successWithDate': 'Deletion scheduled for {{name}}. Permanent removal is planned for {{date}}.',
    
    // Permissions
    'permissions.all': 'All Access',
    'permissions.dashboard': 'Dashboard Access',
    'permissions.assets': 'Assets Access',
    'permissions.compliance': 'Compliance Access',
    'permissions.customerTrust': 'Security Governance & Documentation Access',
    'permissions.suppliers': 'Suppliers Access',
    'permissions.reports': 'Reports Access',
    'permissions.integrations': 'Integrations Access',
    // incidents/alerts removed
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.select': 'Select',
    'common.saved': 'Saved',

    // Starter Guide
    'starter.loading': 'Loading starter guide...',
    'starter.header.title': 'Fill out this Form to Get an Express Check-Up of your Digital Infrastructure',
    'starter.header.p1': 'We’ll scan for risks and give you quick feedback — no technical background required.',
    'starter.header.p2': 'Fast & Confidential.',
    'starter.form.sector': 'Sector*',
    'starter.form.hasIS': 'Operate information systems?*',
    'starter.form.location': 'Where are systems located?',
    'starter.form.processEU': 'Process personal data of EU citizens?',
    'starter.form.dataTypes': 'Data types',
    'starter.form.strategy': 'Information security strategy',
    'starter.form.responsible': 'Responsible for information security risks',
    'starter.form.team': 'Cybersecurity officer/team',
    'starter.form.drp': 'Disaster recovery / incident plan',
    'starter.form.vulnTest': 'Vulnerability testing',
    'starter.form.wantReport': 'Receive short vulnerability report for public systems?',
    'starter.form.publicResources': 'Public resources (domains/IP)',
    'starter.form.standards': 'Relevant standards',
    'starter.form.certAudit': 'Certified audit in 12 months',
    'starter.form.preAudit': 'Interested in pre-audit checkup',
    'starter.form.selfAssess': 'Self-assess using automated platform',
    'starter.form.name': 'Name*',
    'starter.form.companyName': 'Company name*',
    'starter.form.email': 'Email*',
    'starter.form.phone': 'Phone*',
    'starter.form.privacy': 'Privacy Policy accepted',
    'starter.loadFailed': 'Failed to load starter guide',
    'starter.validation.failed': 'Validation failed',
    'starter.validation.required': 'Required',
    'starter.validation.invalid_format': 'Invalid format',
    'starter.validation.invalid_enum': 'Choose a listed value',
    'starter.field.sector': 'Sector',
    'starter.field.contactName': 'Name',
    'starter.field.companyName': 'Company name',
    'starter.field.email': 'Email',
    'starter.field.phone': 'Phone',
    'starter.field.privacyPolicyAccepted': 'Privacy policy',
    'starter.field.systemLocation': 'System location',
    'starter.field.hasSecurityStrategy': 'Security strategy',
    'starter.field.hasCybersecurityTeam': 'Cybersecurity team',
    'starter.field.hasDisasterRecoveryPlan': 'Disaster recovery plan',
    'starter.field.testsVulnerabilities': 'Vulnerability testing',
    'starter.field.plansCertifiedAudit': 'Certified audit',
    'starter.btn.save': 'Save',
    'starter.btn.saving': 'Saving...',
    'starter.summary.title': 'Summary',
    'starter.summary.company': 'Company Name',
    'starter.summary.user': 'User Name',
    'starter.summary.email': 'Email',
    'starter.summary.phone': 'Phone',
    'starter.summary.regSystems': 'Registered Systems',
    'starter.summary.regSystemsHelper': 'Enter domains/IP comma or space-separated',
    'starter.summary.recommended': 'Recommended Standards',
    'starter.btn.editForm': 'Edit Form',
    'starter.btn.exportPdf': 'Export PDF',
    'starter.btn.exportExcel': 'Export Excel',

    // User Settings
    'user.editProfile': 'Edit Profile',
    'user.profileUpdated': 'Profile updated successfully',
    'user.profileNotFound': 'Profile Not Found',
    'user.unableLoad': 'Unable to load user profile.',
    'user.profileInfo': 'Profile Information',
    'user.usernameImmutable': 'Username cannot be changed',
    'user.currentPassword': 'Current Password',
    'user.newPasswordOptional': 'New Password (optional)',
    'user.confirmNewPassword': 'Confirm New Password',
    'user.saveChanges': 'Save Changes',
    'user.accountDetails': 'Account Details',
    'user.memberSince': 'Member Since',
    'user.lastLogin': 'Last Login',
    'user.permissions': 'Permissions',
    'user.yourPermissions': 'Your Permissions',
    'user.updateProfileFailed': 'Failed to update profile',
    'user.changePasswordFailed': 'Failed to change password',
    'auth.passwordsDontMatch': 'Passwords do not match',
  },
  ru: {
    'common.sortBy': 'Сортировать по:',
    'common.filterBySeverity': 'Фильтр по критичности:',
    // App
    'app.name': 'DefendSphere',
    // A11y
    'a11y.languageSwitcher': 'Переключатель языка',
    'a11y.languageSelect': 'Выберите язык',
    'a11y.chatInput': 'Введите сообщение',
    'a11y.chatSend': 'Отправить сообщение',
    // Navigation
    'nav.home': 'Главная',
    'nav.assets': 'Активы',
    'nav.compliance': 'Соответствие',
    'nav.customerTrust': 'Доверие клиентов',
    'nav.suppliers': 'Поставщики',
    'nav.reports': 'Отчеты',
    'nav.starterGuide': 'Руководство',
    'nav.helpAsk': 'Помощь/Запрос',
    'nav.integrations': 'Интеграции',
    'integrations.aiLead':
      'Краткие действия с ключами LLM. Сохранение секретов и выбор активного провайдера выполняются в основном разделе админки «Интеграции» / AI Assistant; на этой странице нет кнопки включения провайдера.',
    'integrations.ai.manageAccess': 'Настроить доступ',
    'integrations.ai.applyAccessChanges': 'Применить изменения',
    'integrations.ai.searchOrganizations': 'Поиск организаций…',
    'integrations.ai.selectAll': 'Выбрать все',
    'integrations.ai.accessGrantedCount': 'Организаций с доступом',
    'integrations.ai.accessPartialFailure':
      'Часть изменений не применена. Окно остаётся открытым — можно повторить.',
    'integrations.ai.noOrganizations': 'Организации не найдены',
    'integrations.ai.noSearchMatches': 'Нет организаций по запросу',
    'integrations.ai.orgsLoadFailed': 'Не удалось загрузить организации',
    'integrations.ai.noPlatformProviders': 'Платформенные провайдеры ещё не настроены.',
    'integrations.ai.accessGranted': 'Доступ выдан',
    'integrations.ai.unknownOrganization': 'Организация',
    'integrations.ai.disabled': 'Отключён',
    'nav.userDashboard': 'Настройки пользователя',
    'nav.notifications': 'Уведомления',
    // incidents/alerts removed
    'nav.settings': 'Настройки',
    'nav.admin': 'Панель администратора',
    
    // Auth
    'auth.login': 'Войти',
    'auth.register': 'Регистрация',
    'auth.logout': 'Выйти',
    'auth.invalidCredentials': 'Неверное имя пользователя или пароль. Попробуйте еще раз.',
    
    // Admin Panel
    'admin.error.fetchUsers': 'Не удалось загрузить пользователей',
    'admin.error.createUser': 'Не удалось создать пользователя',
    'admin.error.updateUser': 'Не удалось обновить пользователя',
    'admin.error.generic': 'Произошла ошибка',
    'admin.placeholder.newOrgName': 'Название новой организации',
    'admin.placeholder.filterByOrg': 'Фильтр по организации...',
    'admin.placeholder.filterByOrgId': 'Фильтр по организации (ID)',
    'admin.placeholder.orgList': 'Орг А, Орг Б',
    'admin.placeholder.ipAddress': '217.65.144.232',
    'admin.placeholder.port': '9392',
    'admin.placeholder.username': 'admin',
    'admin.placeholder.password': '••••••••',
    
    // Assets
    'assets.error.fetchFailed': 'Не удалось загрузить данные активов',
    
    // Reports
    'reports.error.fetchFailed': 'Не удалось загрузить данные отчетов',
    'reports.headerTitle': 'Отчеты по соответствию',
    // Sections
    'suppliers.title': 'Поставщики',
    'suppliers.open': 'Открыть поставщиков',
    'customerTrust.title': 'Управление безопасностью и документация',
    'customerTrust.open': 'Открыть управление',
    'compliance.progress': 'Прогресс соответствия',
    'compliance.open': 'Открыть соответствие',
    'reports.error.organizationNotFound': 'Организация не найдена в сессии',
    'reports.error.uploadFailed': 'Ошибка загрузки',
    'reports.pageSubtitle': 'Просматривайте и экспортируйте отчёты о соответствии',
    'reports.backToDashboard': 'Назад к дашборду',
    'reports.recentTitle': 'Недавние отчёты',
    'reports.recentSubtitle': 'Просматривайте и экспортируйте отчёты о соответствии',
    'reports.table.reportName': 'Название отчёта',
    'reports.table.date': 'Дата',
    'reports.table.status': 'Статус',
    'reports.table.actions': 'Действия',
    'reports.action.view': 'Открыть',
    'reports.action.exportPdf': 'Экспорт PDF',
    
    // Starter Guide
    'starterGuide.options.yes': 'Да',
    'starterGuide.options.no': 'Нет',
    'starterGuide.options.inDevelopment': 'В разработке',
    'starterGuide.options.externalProvider': 'Внешний поставщик',
    'starterGuide.options.notSure': 'Не уверен',
    'starterGuide.options.sometimes': 'Иногда',
    'starterGuide.sectors.banking': 'Банковское дело',
    'starterGuide.sectors.chemical': 'Химическая промышленность',
    'starterGuide.sectors.digitalInfrastructure': 'Цифровая инфраструктура',
    'starterGuide.sectors.digitalServiceProviders': 'Поставщики цифровых услуг',
    'starterGuide.sectors.healthcare': 'Здравоохранение',
    'starterGuide.sectors.transport': 'Транспорт',
    'starterGuide.sectors.other': 'Другое',
    'starterGuide.locations.onPremise': 'На территории',
    'starterGuide.locations.rented': 'Арендованные',
    'starterGuide.locations.cloud': 'Облако',
    'starterGuide.locations.notSure': 'Не уверен',
    'starterGuide.dataTypes.name': 'Имя',
    'starterGuide.dataTypes.email': 'Email',
    'starterGuide.dataTypes.phone': 'Телефон',
    'starterGuide.dataTypes.financial': 'Финансовые данные',
    'starterGuide.dataTypes.address': 'Адрес',
    
    // Error Pages
    'error.somethingWentWrong': 'Что-то пошло не так',
    'error.unexpectedError': 'Извините, произошла неожиданная ошибка. Попробуйте обновить страницу.',
    'error.details': 'Детали ошибки (Разработка)',
    'error.componentStack': 'Стек компонентов:',
    'error.tryAgain': 'Попробовать снова',
    'error.refreshPage': 'Обновить страницу',
    
    'auth.username': 'Имя пользователя',
    'auth.password': 'Пароль',
    'auth.email': 'Email',
    'auth.welcome': 'Добро пожаловать',
    'auth.signInToDashboard': 'Войдите в панель безопасности',
    'auth.defaultUsers': 'Пользователи по умолчанию:',
    'auth.signingIn': 'Вход...',
    'auth.connectionError': 'Ошибка подключения. Попробуйте снова.',
    'auth.loginFailed': 'Ошибка входа',
    
    // Dashboard
    'dashboard.title': 'Панель безопасности',
    'dashboard.incidentsMonitoring': 'Мониторинг инцидентов',
    'dashboard.openReports': 'Открыть отчеты',
    'dashboard.assets': 'Активы',
    'dashboard.incidents': 'Инциденты',
    'dashboard.criticalIncidents': 'Критические инциденты',
    'dashboard.noData': 'Нет данных',
    // Scan Request
    'scan.request.title': 'Запустить на сканирование активов',
    'scan.start_scan': 'Запустить сканирование',
    'scan.scanner_type': 'Тип сканера',
    'scan.openvas_full': 'OpenVAS (сетевой / инфраструктурный сканер)',
    'scan.active_scans': 'Активные сканы',
    'scan.no_active_scans': 'Нет активных сканов',
    'scan.cve_scanner': 'CVE Scanner (NVD / SCAP / OVAL)',
    'scan.update_status': 'Обновить статус',
    'scan.download_report': 'Скачать отчет',
    'scan.details': 'Подробно',
    'scan.col.taskId': 'Task ID',
    'scan.col.name': 'Название',
    'scan.col.target': 'Цель',
    'scan.col.status': 'Статус',
    'scan.col.progress': 'Прогресс',
    'scan.col.startedAt': 'Начало',
    'scan.col.reportId': 'Report ID',
    'scan.col.actions': 'Действия',
    'scan.col.config': 'Конфигурация',
    'scan.col.scanner': 'Сканер',
    'scan.request.assetsAvailable': '{count} актив(ов) доступно для сканирования',
    'scan.request.yourAssets': 'Ваши активы:',
    'scan.request.manualInput': 'Ручной ввод (DNS/IP + порт)',
    'scan.request.exampleHost': 'example.com или 1.2.3.4',
    'scan.request.port': 'порт',
    'scan.request.addResource': '+ Добавить ресурс',
    'scan.request.submit': 'Отправить запрос на сканирование',
    'scan.starting': 'Запуск сканирования...',
    'scan.started': 'Сканирование успешно запущено',
    'scan.failed': 'Не удалось запустить сканирование',
    'scan.assetType.webServer': 'Веб-сервер',
    'scan.environment.production': 'Продакшн',

    // Help Request
    'help.request.title': 'Подать Запрос / Запросить помощь',
    'help.request.placeholder': 'Опишите проблему или запрос...',
    'help.request.submit': 'Отправить обращение',
    'help.request.noFile': 'Файл не выбран',
    'help.request.submitButton': 'Отправить обращение',
    'compliance.fillIn': 'Заполнить',
    'compliance.survey.title': 'Анкета: {standard}',
    'compliance.survey.hide': 'Скрыть',
    'compliance.survey.save': 'Сохранить',
    'compliance.survey.cancel': 'Отмена',
    'compliance.survey.loading': 'Загрузка анкеты…',
    'compliance.survey.ratings': 'Оценки: 1 — не выполняется; 2 — частично; 3 — средний уровень; 4 — хороший уровень; 5 — полное соответствие.',
    'compliance.survey.lastSavedLabel': 'Последнее сохранение',
    'compliance.survey.byUser': '(админ {user})',
    'compliance.survey.currentScoreLabel': 'Текущий балл',
    'assets.organizationTitle': 'Активы организации: {orgName}',
      'reports.title': 'Отчёты организации: {orgName}',
      'reports.organizationTitle': 'Организация: {orgName}',
    'reports.summary': 'Сводка',
    'reports.dragDrop': 'Перетащите PDF сюда',
    'reports.uploadPdf': 'Загрузить PDF',
    'reports.chooseFile': 'Выберите файл',
    'reports.uploadCta': 'Загрузить отчет об уязвимостях (CSV/PDF/Excel)',

    // Assistant
    'assistant.title': 'Помощник безопасности',
    'assistant.greeting': '👋 Привет! Я ваш помощник по безопасности на базе Gemini AI. Чем могу помочь?',
    'assistant.unavailable': 'Ассистент недоступен. Попробуйте позже.',
    'assistant.integrationHint': 'AI ассистент недоступен. Настройте интеграцию в Admin Panel → Integrations',
    'assistant.open': 'Открыть чат',
    'assistant.close': 'Закрыть чат',
    'assistant.placeholder': 'Введите сообщение…',
    'assistant.send': 'Отправить',
    'assistant.gemini.configured': 'AI ассистент Gemini настроен и готов к работе',
    'assistant.gemini.testing': 'Тестирование соединения с Gemini...',
    'assistant.typing': 'Ассистент печатает...',
    // GDPR Survey Sections (Russian)
    'gdpr.section1.title': '1. Законодательная основа и законность обработки данных',
    'gdpr.section1.q1': 'Определены ли основания для обработки персональных данных?',
    'gdpr.section1.q2': 'Формируется ли политика конфиденциальности и получается ли согласие пользователей?',
    'gdpr.section1.q3': 'Можно ли субъектам данных отказаться от обработки своих данных?',
    'gdpr.section2.title': '2. Ясность и прозрачность взаимодействия с пользователями',
    'gdpr.section2.q1': 'Предоставляется ли информация о целях и способах обработки данных?',
    'gdpr.section2.q2': 'Всегда ли доступно уведомление о правах субъектов данных?',
    'gdpr.section2.q3': 'Легко ли ознакомиться с порядком обработки данных?',
    'gdpr.section3.title': '3. Ограниченность целей обработки данных',
    'gdpr.section3.q1': 'Ограничиваете ли сбор данных только необходимыми категориями?',
    'gdpr.section3.q2': 'Устанавливаете ли сроки удаления данных?',
    'gdpr.section4.title': '4. Качество и точность данных',
    'gdpr.section4.q1': 'Проверяете ли актуальность и правильность данных?',
    'gdpr.section4.q2': 'Ведёте ли мониторинг изменений и ошибок?',
    'gdpr.section5.title': '5. Безопасность хранения и передачи данных',
    'gdpr.section5.q1': 'Применяются ли современные меры защиты (шифрование, ограничение доступа)?',
    'gdpr.section5.q2': 'Установлена ли система предупреждения и предотвращения утечек?',
    'gdpr.section6.title': '6. Ответственность и подотчётность',
    'gdpr.section6.q1': 'Назначено ли должностное лицо по защите данных (DPO)?',
    'gdpr.section6.q2': 'Хранится ли документация по обработке данных?',
    'gdpr.section7.title': '7. Трансграничная передача данных',
    'gdpr.section7.q1': 'Используются ли гарантии при передаче данных за границу?',
    'gdpr.section7.q2': 'Есть ли соглашения о конфиденциальности с зарубежными партнёрами?',
    'gdpr.section8.title': '8. Инцидент-менеджмент и реагирование',
    'gdpr.section8.q1': 'Разработана ли система обнаружения и реагирования на инциденты?',
    'gdpr.section8.q2': 'Сообщаете ли о нарушениях надзорным органам и заинтересованным сторонам?',
    'gdpr.section9.title': '9. Обучение сотрудников и осведомлённость',
    'gdpr.section9.q1': 'Организуется ли обучение сотрудников по вопросам GDPR?',
    'gdpr.section9.q2': 'Проводятся ли внутренние тренинги?',
    // DORA Survey Sections (Russian)
    'dora.section1.title': '1. Управление цифровыми рисками',
    'dora.section1.q1': 'Создан ли механизм управления цифровыми рисками?',
    'dora.section1.q2': 'Присваиваются ли конкретные роли и полномочия персоналу?',
    'dora.section1.q3': 'Действенна ли система отслеживания и ликвидации угроз?',
    'dora.section2.title': '2. Уровень технологического оснащения',
    'dora.section2.q1': 'Соответствует ли инфраструктура современным требованиям надежности?',
    'dora.section2.q2': 'Обеспечен ли мониторинг и анализ технологических рисков?',
    'dora.section2.q3': 'Современные ли используемые технологии?',
    'dora.section3.title': '3. Кадровый потенциал и квалификация',
    'dora.section3.q1': 'Обучаются ли сотрудники методикам работы с цифровыми рисками?',
    'dora.section3.q2': 'Выделяются ли ресурсы на повышение квалификации?',
    'dora.section3.q3': 'Есть ли опыт участия в тренировочных учениях?',
    'dora.section4.title': '4. Внутренняя культура устойчивости',
    'dora.section4.q1': 'Демонстрирует ли руководство личную приверженность культуре устойчивости?',
    'dora.section4.q2': 'Воспринимается ли важность устойчивости сотрудниками?',
    'dora.section4.q3': 'Признаётся ли ценность устойчивости высшим руководством?',
    'dora.section5.title': '5. Внешние взаимоотношения и партнёрство',
    'dora.section5.q1': 'Сотрудничает ли организация с партнёрами по вопросам устойчивости?',
    'dora.section5.q2': 'Совместимы ли практики партнёров с подходами организации?',
    'dora.section5.q3': 'Влияет ли участие в профессиональных сообществах на стратегию?',
    'dora.section6.title': '6. Трансмиссия рисков и ответственность',
    'dora.section6.q1': 'Упорядочены ли правила передачи рисков и ответственности?',
    'dora.section6.q2': 'Способны ли структуры оперативно реагировать на возникающие риски?',
    'dora.section6.q3': 'Готова ли организация к повышенным нагрузкам в кризис?',
    'dora.section7.title': '7. Инновационное развитие',
    'dora.section7.q1': 'Инвестирует ли организация в новые технологии и услуги?',
    'dora.section7.q2': 'Помогает ли партнёрам в повышении их устойчивости?',
    'dora.section7.q3': 'Следит ли за последними трендами и технологиями?',
    'dora.section8.title': '8. Отчётность и коммуникации',
    'dora.section8.q1': 'Организована ли доступная отчетность по цифровой устойчивости?',
    'dora.section8.q2': 'Поддерживается ли прозрачность коммуникаций?',
    'dora.section8.q3': 'Ведётся ли журнал инцидентов и отказов?',
    'dashboard.threatsOverTime': 'Угрозы по времени',
    'dashboard.threatTypes': 'Типы угроз',
    'dashboard.recentIncidents': 'Недавние инциденты',
    'dashboard.time': 'Время',
    'dashboard.type': 'Тип',
    'dashboard.severity': 'Серьезность',
    'dashboard.status': 'Статус',
    'dashboard.high': 'Высокая',
    'dashboard.medium': 'Средняя',
    'dashboard.low': 'Низкая',
    
    // Admin Panel
    'admin.title': 'Панель администратора',
    'admin.users': 'Пользователи',
    'admin.addUser': 'Добавить пользователя',
    'admin.editUser': 'Редактировать пользователя',
    'admin.deleteUser': 'Удалить пользователя',
    'admin.userManagement': 'Управление пользователями',
    'admin.role': 'Роль',
    'admin.permissions': 'Разрешения',
    'admin.createdAt': 'Создан',
    'admin.lastLogin': 'Последний вход',
    'admin.actions': 'Действия',
    'admin.admin': 'Администратор',
    'admin.user': 'Пользователь',
    'admin.save': 'Сохранить',
    'admin.cancel': 'Отмена',
    'admin.delete': 'Удалить',
    'admin.confirmDelete': 'Вы уверены, что хотите удалить этого пользователя?',
    'admin.userCreated': 'Пользователь успешно создан',
    'admin.userUpdated': 'Пользователь успешно обновлен',
    'admin.userDeleted': 'Пользователь успешно удален',
    'admin.error': 'Произошла ошибка',
    'admin.attachReportPdf': 'Прикрепить отчет (PDF)',
    'admin.uploadReport': 'Загрузить отчёт',
    'admin.uploadReportFailed': 'Не удалось загрузить отчёт',
    'admin.orgDeletion.title': 'Запланировать удаление организации',
    'admin.orgDeletion.warnTitle': 'Это действие запускает необратимое удаление',
    'admin.orgDeletion.warnBody': 'Организация будет сохранена 5 дней, затем удалена безвозвратно. Пользователи и активы, связанные с организацией, могут потерять доступ.',
    'admin.orgDeletion.organizationLabel': 'Организация',
    'admin.orgDeletion.confirmNameLabel': 'Введите имя организации для подтверждения',
    'admin.orgDeletion.confirmNameHint': 'Введите имя точно как указано выше (регистр и пробелы важны).',
    'admin.orgDeletion.nameMismatchInline': 'Имя не совпадает. Ожидается: {{name}}',
    'admin.orgDeletion.nameMismatch': 'Введённое имя не совпадает с именем организации. Скопируйте его точно, включая регистр и пробелы.',
    'admin.orgDeletion.commentLabel': 'Причина удаления (аудит)',
    'admin.orgDeletion.commentPlaceholder': 'Опишите, почему организацию нужно удалить…',
    'admin.orgDeletion.commentHint': 'Комментарий обязателен и будет сохранён в журнале аудита.',
    'admin.orgDeletion.commentTooShortInline': 'Минимум 5 символов.',
    'admin.orgDeletion.commentTooShort': 'Укажите причину не короче 5 символов. Она будет сохранена в журнале аудита.',
    'admin.orgDeletion.alreadyScheduled': 'Удаление этой организации уже запланировано.',
    'admin.orgDeletion.notFound': 'Организация не найдена. Обновите страницу и повторите попытку.',
    'admin.orgDeletion.genericError': 'Не удалось запланировать удаление организации.',
    'admin.orgDeletion.submit': 'Запланировать удаление',
    'admin.orgDeletion.submitting': 'Планирование…',
    'admin.orgDeletion.success': 'Удаление организации {{name}} запланировано.',
    'admin.orgDeletion.successWithDate': 'Удаление {{name}} запланировано. Безвозвратное удаление: {{date}}.',
    
    // Permissions
    'permissions.all': 'Полный доступ',
    'permissions.dashboard': 'Доступ к панели',
    'permissions.assets': 'Доступ к активам',
    'permissions.compliance': 'Доступ к соответствию',
    'permissions.customerTrust': 'Доступ к управлению безопасностью и документации',
    'permissions.suppliers': 'Доступ к поставщикам',
    'permissions.reports': 'Доступ к отчетам',
    'permissions.integrations': 'Доступ к интеграциям',
    // incidents/alerts removed
    
    // Common
    'common.loading': 'Загрузка...',
    'common.error': 'Ошибка',
    'common.success': 'Успешно',
    'common.save': 'Сохранить',
    'common.saving': 'Сохранение…',
    'common.cancel': 'Отмена',
    'common.delete': 'Удалить',
    'common.edit': 'Редактировать',
    'common.add': 'Добавить',
    'common.close': 'Закрыть',
    'common.yes': 'Да',
    'common.no': 'Нет',
    'common.select': 'Выбрать',
    'common.saved': 'Сохранено',

    // Starter Guide
    'starter.loading': 'Загрузка анкеты...',
    'starter.header.title': 'Заполните форму для экспресс‑проверки вашей цифровой инфраструктуры',
    'starter.header.p1': 'Мы проверим риски и дадим быстрый фидбек — без технических знаний.',
    'starter.header.p2': 'Быстро и конфиденциально.',
    'starter.form.sector': 'Сектор*',
    'starter.form.hasIS': 'Эксплуатируете информационные системы?*',
    'starter.form.location': 'Где расположены системы?',
    'starter.form.processEU': 'Обрабатываете персональные данные граждан ЕС?',
    'starter.form.dataTypes': 'Типы данных',
    'starter.form.strategy': 'Стратегия по ИБ',
    'starter.form.responsible': 'Ответственный за риски ИБ',
    'starter.form.team': 'Отдел/специалист по кибербезопасности',
    'starter.form.drp': 'План восстановления/реагирования на инциденты',
    'starter.form.vulnTest': 'Проводите тестирование на уязвимости?',
    'starter.form.wantReport': 'Получить краткий отчет по публичным системам?',
    'starter.form.publicResources': 'Публичные ресурсы (домены/IP)',
    'starter.form.standards': 'Актуальные стандарты',
    'starter.form.certAudit': 'Сертифицированный аудит в 12 месяцев',
    'starter.form.preAudit': 'Интересует предварительная проверка',
    'starter.form.selfAssess': 'Самооценка на платформе',
    'starter.form.name': 'Имя*',
    'starter.form.companyName': 'Компания*',
    'starter.form.email': 'Email*',
    'starter.form.phone': 'Телефон*',
    'starter.form.privacy': 'Политика конфиденциальности принята',
    'starter.loadFailed': 'Не удалось загрузить стартовую анкету',
    'starter.validation.failed': 'Проверка не пройдена',
    'starter.validation.required': 'Обязательное поле',
    'starter.validation.invalid_format': 'Неверный формат',
    'starter.validation.invalid_enum': 'Выберите значение из списка',
    'starter.field.sector': 'Сектор',
    'starter.field.contactName': 'Имя',
    'starter.field.companyName': 'Компания',
    'starter.field.email': 'Email',
    'starter.field.phone': 'Телефон',
    'starter.field.privacyPolicyAccepted': 'Политика конфиденциальности',
    'starter.field.systemLocation': 'Расположение систем',
    'starter.field.hasSecurityStrategy': 'Стратегия безопасности',
    'starter.field.hasCybersecurityTeam': 'Команда кибербезопасности',
    'starter.field.hasDisasterRecoveryPlan': 'План восстановления',
    'starter.field.testsVulnerabilities': 'Проверка уязвимостей',
    'starter.field.plansCertifiedAudit': 'Сертифицированный аудит',
    'starter.btn.save': 'Сохранить',
    'starter.btn.saving': 'Сохранение...',
    'starter.summary.title': 'Сводка',
    'starter.summary.company': 'Компания',
    'starter.summary.user': 'Пользователь',
    'starter.summary.email': 'Email',
    'starter.summary.phone': 'Телефон',
    'starter.summary.regSystems': 'Зарегистрированные системы',
    'starter.summary.regSystemsHelper': 'Введите домены/IP через запятую или пробел',
    'starter.summary.recommended': 'Рекомендованные стандарты',
    'starter.btn.editForm': 'Редактировать форму',
    'starter.btn.exportPdf': 'Экспорт PDF',
    'starter.btn.exportExcel': 'Экспорт Excel',

    // User Settings
    'user.editProfile': 'Редактировать профиль',
    'user.profileUpdated': 'Профиль успешно обновлен',
    'user.profileNotFound': 'Профиль не найден',
    'user.unableLoad': 'Не удалось загрузить профиль пользователя.',
    'user.profileInfo': 'Информация профиля',
    'user.usernameImmutable': 'Имя пользователя нельзя изменить',
    'user.currentPassword': 'Текущий пароль',
    'user.newPasswordOptional': 'Новый пароль (необязательно)',
    'user.confirmNewPassword': 'Подтвердите новый пароль',
    'user.saveChanges': 'Сохранить изменения',
    'user.accountDetails': 'Детали аккаунта',
    'user.memberSince': 'Участник с',
    'user.lastLogin': 'Последний вход',
    'user.permissions': 'Разрешения',
    'user.yourPermissions': 'Ваши разрешения',
    'user.updateProfileFailed': 'Не удалось обновить профиль',
    'user.changePasswordFailed': 'Не удалось изменить пароль',
    'auth.passwordsDontMatch': 'Пароли не совпадают',

  },
  es: {
    'common.sortBy': 'Ordenar por:',
    'common.filterBySeverity': 'Filtrar por severidad:',
    // App
    'app.name': 'DefendSphere',
    // A11y
    'a11y.languageSwitcher': 'Selector de idioma',
    'a11y.languageSelect': 'Seleccionar idioma',
    'a11y.chatInput': 'Escribe tu mensaje',
    'a11y.chatSend': 'Enviar mensaje',
    // Navigation
    'nav.home': 'Inicio',
    'nav.assets': 'Activos',
    'nav.compliance': 'Cumplimiento',
    'nav.customerTrust': 'Confianza del Cliente',
    'nav.suppliers': 'Proveedores',
    'nav.reports': 'Informes',
    'nav.starterGuide': 'Guía de Inicio',
    'nav.helpAsk': 'Ayuda',
    'nav.integrations': 'Integraciones',
    'integrations.aiLead':
      'Atajos opcionales para claves LLM. Guardar secretos y elegir el proveedor activo son acciones de administrador en Integraciones / AI Assistant; esta página no activa un proveedor.',
    'integrations.ai.manageAccess': 'Gestionar acceso',
    'integrations.ai.applyAccessChanges': 'Aplicar cambios',
    'integrations.ai.searchOrganizations': 'Buscar organizaciones…',
    'integrations.ai.selectAll': 'Seleccionar todo',
    'integrations.ai.accessGrantedCount': 'Organizaciones con acceso',
    'integrations.ai.accessPartialFailure':
      'Algunos cambios fallaron. La ventana permanece abierta para reintentar.',
    'integrations.ai.noOrganizations': 'No se encontraron organizaciones',
    'integrations.ai.noSearchMatches': 'Ninguna organización coincide con la búsqueda',
    'integrations.ai.orgsLoadFailed': 'No se pudieron cargar las organizaciones',
    'integrations.ai.noPlatformProviders': 'Aún no hay proveedores de plataforma.',
    'integrations.ai.accessGranted': 'Acceso concedido',
    'integrations.ai.unknownOrganization': 'Organización',
    'integrations.ai.disabled': 'Deshabilitado',
    'nav.userDashboard': 'Configuración del Usuario',
    'nav.notifications': 'Notificaciones',
    // incidents/alerts removed
    'nav.settings': 'Configuración',
    'nav.admin': 'Panel de Administración',
    
    // Auth
    'auth.login': 'Iniciar Sesión',
    'auth.register': 'Registrarse',
    'auth.logout': 'Cerrar Sesión',
    'auth.invalidCredentials': 'Nombre de usuario o contraseña inválidos. Inténtalo de nuevo.',
    
    // Admin Panel
    'admin.error.fetchUsers': 'Error al cargar usuarios',
    'admin.error.createUser': 'Error al crear usuario',
    'admin.error.updateUser': 'Error al actualizar usuario',
    'admin.error.generic': 'Ocurrió un error',
    'admin.placeholder.newOrgName': 'Nombre de nueva organización',
    'admin.placeholder.filterByOrg': 'Filtrar por organización...',
    'admin.placeholder.filterByOrgId': 'Filtrar por organización (ID)',
    'admin.placeholder.orgList': 'Org A, Org B',
    'admin.placeholder.ipAddress': '217.65.144.232',
    'admin.placeholder.port': '9392',
    'admin.placeholder.username': 'admin',
    'admin.placeholder.password': '••••••••',
    
    // Assets
    'assets.error.fetchFailed': 'Error al cargar datos de activos',
    
    // Reports
    'reports.error.fetchFailed': 'Error al cargar datos de reportes',
    'reports.headerTitle': 'Informes de Cumplimiento',
    // Sections
    'suppliers.title': 'Proveedores',
    'suppliers.open': 'Abrir Proveedores',
    'customerTrust.title': 'Confianza del Cliente',
    'customerTrust.open': 'Abrir Clientes',
    'compliance.progress': 'Progreso de Cumplimiento',
    'compliance.open': 'Abrir Cumplimiento',
    'reports.error.organizationNotFound': 'Organización no encontrada en la sesión',
    'reports.error.uploadFailed': 'Error de carga',
    'reports.pageSubtitle': 'Consulte y exporte sus informes de cumplimiento',
    'reports.backToDashboard': 'Volver al panel',
    'reports.recentTitle': 'Informes recientes',
    'reports.recentSubtitle': 'Consulte y exporte sus informes de cumplimiento',
    'reports.table.reportName': 'Nombre del informe',
    'reports.table.date': 'Fecha',
    'reports.table.status': 'Estado',
    'reports.table.actions': 'Acciones',
    'reports.action.view': 'Ver',
    'reports.action.exportPdf': 'Exportar PDF',
    
    // Starter Guide
    'starterGuide.options.yes': 'Sí',
    'starterGuide.options.no': 'No',
    'starterGuide.options.inDevelopment': 'En desarrollo',
    'starterGuide.options.externalProvider': 'Proveedor externo',
    'starterGuide.options.notSure': 'No estoy seguro',
    'starterGuide.options.sometimes': 'A veces',
    'starterGuide.sectors.banking': 'Banca',
    'starterGuide.sectors.chemical': 'Industria química',
    'starterGuide.sectors.digitalInfrastructure': 'Infraestructura digital',
    'starterGuide.sectors.digitalServiceProviders': 'Proveedores de servicios digitales',
    'starterGuide.sectors.healthcare': 'Salud',
    'starterGuide.sectors.transport': 'Transporte',
    'starterGuide.sectors.other': 'Otro',
    'starterGuide.locations.onPremise': 'En las instalaciones',
    'starterGuide.locations.rented': 'Alquilado',
    'starterGuide.locations.cloud': 'Nube',
    'starterGuide.locations.notSure': 'No estoy seguro',
    'starterGuide.dataTypes.name': 'Nombre',
    'starterGuide.dataTypes.email': 'Email',
    'starterGuide.dataTypes.phone': 'Teléfono',
    'starterGuide.dataTypes.financial': 'Datos financieros',
    'starterGuide.dataTypes.address': 'Dirección',
    
    // Error Pages
    'error.somethingWentWrong': 'Algo salió mal',
    'error.unexpectedError': 'Lo sentimos, pero ocurrió algo inesperado. Por favor, intenta actualizar la página.',
    'error.details': 'Detalles del Error (Desarrollo)',
    'error.componentStack': 'Pila de Componentes:',
    'error.tryAgain': 'Intentar de nuevo',
    'error.refreshPage': 'Actualizar página',
    
    'auth.username': 'Nombre de Usuario',
    'auth.password': 'Contraseña',
    'auth.email': 'Email',
    'auth.welcome': 'Bienvenido',
    'auth.signInToDashboard': 'Inicia sesión en tu panel de seguridad',
    'auth.defaultUsers': 'Usuarios por defecto:',
    'auth.signingIn': 'Iniciando sesión...',
    'auth.connectionError': 'Error de conexión. Inténtalo de nuevo.',
    'auth.loginFailed': 'Error de inicio de sesión',
    
    // Dashboard
    'dashboard.title': 'Panel de Seguridad',
    'dashboard.incidentsMonitoring': 'Monitoreo de Incidentes',
    'dashboard.openReports': 'Abrir Informes',
    'dashboard.assets': 'Activos',
    'dashboard.incidents': 'Incidentes',
    'dashboard.criticalIncidents': 'Incidentes Críticos',
    'dashboard.noData': 'Sin datos',
    // Scan Request
    'scan.request.title': 'Lanzar solicitud de escaneo de activos',
    'scan.start_scan': 'Iniciar escaneo',
    'scan.scanner_type': 'Tipo de escáner',
    'scan.openvas_full': 'OpenVAS (escáner de red/infraestructura)',
    'scan.active_scans': 'Escaneos activos',
    'scan.no_active_scans': 'No hay escaneos activos',
    'scan.cve_scanner': 'CVE Scanner (NVD / SCAP / OVAL)',
    'scan.update_status': 'Actualizar estado',
    'scan.download_report': 'Descargar informe',
    'scan.details': 'Detalles',
    'scan.col.taskId': 'Task ID',
    'scan.col.name': 'Nombre',
    'scan.col.target': 'Objetivo',
    'scan.col.status': 'Estado',
    'scan.col.progress': 'Progreso',
    'scan.col.startedAt': 'Inicio',
    'scan.col.reportId': 'Reporte ID',
    'scan.col.actions': 'Acciones',
    'scan.col.config': 'Configuración',
    'scan.col.scanner': 'Escáner',
    'scan.request.assetsAvailable': '{count} activos disponibles para escanear',
    'scan.request.yourAssets': 'Tus activos:',
    'scan.request.manualInput': 'Entrada manual (DNS/IP + puerto)',
    'scan.request.exampleHost': 'example.com o 1.2.3.4',
    'scan.request.port': 'puerto',
    'scan.request.addResource': '+ Agregar recurso',
    'scan.request.submit': 'Enviar solicitud de escaneo',
    'scan.starting': 'Iniciando escaneo...',
    'scan.started': 'Escaneo iniciado exitosamente',
    'scan.failed': 'Error al iniciar escaneo',
    'scan.assetType.webServer': 'Servidor Web',
    'scan.environment.production': 'Producción',

    // Help Request
    'help.request.title': 'Enviar Solicitud / Pedir Ayuda',
    'help.request.placeholder': 'Describa el problema o la solicitud...',
    'help.request.submit': 'Enviar solicitud',
    'help.request.noFile': 'No se ha seleccionado ningún archivo',
    'help.request.submitButton': 'Enviar Solicitud',
    'compliance.fillIn': 'Completar',
    'compliance.survey.title': 'Encuesta: {standard}',
    'compliance.survey.hide': 'Ocultar',
    'compliance.survey.save': 'Guardar',
    'compliance.survey.cancel': 'Cancelar',
    'compliance.survey.loading': 'Cargando encuesta...',
    'compliance.survey.ratings': 'Calificaciones: 1 — no implementado; 2 — parcialmente; 3 — nivel promedio; 4 — buen nivel; 5 — cumplimiento completo.',
    'compliance.survey.lastSavedLabel': 'Última guardado',
    'compliance.survey.byUser': '(por {user})',
    'compliance.survey.currentScoreLabel': 'Puntuación actual',
    'assets.organizationTitle': 'Activos de la organización: {orgName}',
      'reports.title': 'Informes de la organización: {orgName}',
      'reports.organizationTitle': 'Organización: {orgName}',
    'reports.summary': 'Resumen',
    'reports.dragDrop': 'Arrastra y suelta el PDF aquí',
    'reports.uploadPdf': 'Subir PDF',
    'reports.chooseFile': 'Seleccionar archivo',
    'reports.uploadCta': 'Subir informe de vulnerabilidades (CSV/PDF/Excel)',

    // Assistant
    'assistant.title': 'Asistente de Seguridad',
    'assistant.greeting': '👋 ¡Hola! Soy tu Asistente de Seguridad con Gemini AI. ¿En qué puedo ayudarte?',
    'assistant.unavailable': 'El asistente no está disponible. Inténtalo más tarde.',
    'assistant.integrationHint': 'El Asistente de IA no está disponible. Configúralo en Admin Panel → Integrations.',
    'assistant.open': 'Abrir chat',
    'assistant.close': 'Cerrar chat',
    'assistant.placeholder': 'Escribe tu mensaje…',
    'assistant.send': 'Enviar',
    'assistant.gemini.configured': 'Asistente de IA Gemini configurado y listo',
    'assistant.gemini.testing': 'Probando conexión con Gemini...',
    'assistant.typing': 'El asistente está escribiendo...',
    // GDPR Survey Sections (Spanish)
    'gdpr.section1.title': '1. Base legal y licitud del procesamiento de datos',
    'gdpr.section1.q1': '¿Están definidos los fundamentos para el procesamiento de datos personales?',
    'gdpr.section1.q2': '¿Se forma una política de privacidad y se obtiene el consentimiento de los usuarios?',
    'gdpr.section1.q3': '¿Pueden los sujetos de datos rechazar el procesamiento de sus datos?',
    'gdpr.section2.title': '2. Claridad y transparencia en la interacción con usuarios',
    'gdpr.section2.q1': '¿Se proporciona información sobre los propósitos y métodos de procesamiento de datos?',
    'gdpr.section2.q2': '¿Está siempre disponible la notificación sobre los derechos de los sujetos de datos?',
    'gdpr.section2.q3': '¿Es fácil familiarizarse con el procedimiento de procesamiento de datos?',
    'gdpr.section3.title': '3. Limitación de propósitos de procesamiento de datos',
    'gdpr.section3.q1': '¿Limita la recopilación de datos solo a categorías necesarias?',
    'gdpr.section3.q2': '¿Establece plazos de eliminación de datos?',
    'gdpr.section4.title': '4. Calidad y precisión de datos',
    'gdpr.section4.q1': '¿Verifica la relevancia y corrección de los datos?',
    'gdpr.section4.q2': '¿Monitorea cambios y errores?',
    'gdpr.section5.title': '5. Seguridad del almacenamiento y transmisión de datos',
    'gdpr.section5.q1': '¿Se aplican medidas de protección modernas (cifrado, restricciones de acceso)?',
    'gdpr.section5.q2': '¿Está establecido un sistema para prevenir y detectar filtraciones?',
    'gdpr.section6.title': '6. Responsabilidad y rendición de cuentas',
    'gdpr.section6.q1': '¿Está designado un Oficial de Protección de Datos (DPO)?',
    'gdpr.section6.q2': '¿Se almacena documentación sobre el procesamiento de datos?',
    'gdpr.section7.title': '7. Transferencia transfronteriza de datos',
    'gdpr.section7.q1': '¿Se utilizan garantías al transferir datos al extranjero?',
    'gdpr.section7.q2': '¿Hay acuerdos de confidencialidad con socios extranjeros?',
    'gdpr.section8.title': '8. Gestión y respuesta a incidentes',
    'gdpr.section8.q1': '¿Está desarrollado un sistema para detectar y responder a incidentes?',
    'gdpr.section8.q2': '¿Reporta violaciones a autoridades supervisoras y partes interesadas?',
    'gdpr.section9.title': '9. Capacitación y concienciación de empleados',
    'gdpr.section9.q1': '¿Se organiza capacitación de empleados sobre temas de GDPR?',
    'gdpr.section9.q2': '¿Se realizan entrenamientos internos?',
    // DORA Survey Sections (Spanish)
    'dora.section1.title': '1. Gestión de riesgos digitales',
    'dora.section1.q1': '¿Se ha creado un mecanismo de gestión de riesgos digitales?',
    'dora.section1.q2': '¿Se asignan roles y autoridades específicos al personal?',
    'dora.section1.q3': '¿Es efectivo el sistema de seguimiento y eliminación de amenazas?',
    'dora.section2.title': '2. Nivel de equipamiento tecnológico',
    'dora.section2.q1': '¿La infraestructura cumple con los requisitos modernos de confiabilidad?',
    'dora.section2.q2': '¿Está asegurado el monitoreo y análisis de riesgos tecnológicos?',
    'dora.section2.q3': '¿Son modernas las tecnologías utilizadas?',
    'dora.section3.title': '3. Potencial del personal y calificaciones',
    'dora.section3.q1': '¿Se capacita a los empleados en métodos de gestión de riesgos digitales?',
    'dora.section3.q2': '¿Se asignan recursos para el desarrollo profesional?',
    'dora.section3.q3': '¿Hay experiencia en participar en ejercicios de entrenamiento?',
    'dora.section4.title': '4. Cultura interna de resiliencia',
    'dora.section4.q1': '¿La gerencia demuestra compromiso personal con la cultura de resiliencia?',
    'dora.section4.q2': '¿Los empleados perciben la importancia de la resiliencia?',
    'dora.section4.q3': '¿La alta gerencia reconoce el valor de la resiliencia?',
    'dora.section5.title': '5. Relaciones externas y asociaciones',
    'dora.section5.q1': '¿La organización colabora con socios en temas de resiliencia?',
    'dora.section5.q2': '¿Las prácticas de los socios son compatibles con los enfoques de la organización?',
    'dora.section5.q3': '¿La participación en comunidades profesionales influye en la estrategia?',
    'dora.section6.title': '6. Transmisión de riesgos y responsabilidad',
    'dora.section6.q1': '¿Están organizadas las reglas para la transferencia de riesgos y responsabilidad?',
    'dora.section6.q2': '¿Las estructuras son capaces de responder rápidamente a riesgos emergentes?',
    'dora.section6.q3': '¿Está la organización lista para cargas aumentadas en crisis?',
    'dora.section7.title': '7. Desarrollo innovador',
    'dora.section7.q1': '¿La organización invierte en nuevas tecnologías y servicios?',
    'dora.section7.q2': '¿Ayuda a los socios a mejorar su resiliencia?',
    'dora.section7.q3': '¿Sigue las últimas tendencias y tecnologías?',
    'dora.section8.title': '8. Reportes y comunicaciones',
    'dora.section8.q1': '¿Está organizado el reporte accesible sobre resiliencia digital?',
    'dora.section8.q2': '¿Se mantiene la transparencia de las comunicaciones?',
    'dora.section8.q3': '¿Se mantiene un registro de incidentes y fallas?',
    'dashboard.threatsOverTime': 'Amenazas en el Tiempo',
    'dashboard.threatTypes': 'Tipos de Amenazas',
    'dashboard.recentIncidents': 'Incidentes Recientes',
    'dashboard.time': 'Tiempo',
    'dashboard.type': 'Tipo',
    'dashboard.severity': 'Severidad',
    'dashboard.status': 'Estado',
    'dashboard.high': 'Alta',
    'dashboard.medium': 'Media',
    'dashboard.low': 'Baja',
    
    // Admin Panel
    'admin.title': 'Panel de Administración',
    'admin.users': 'Usuarios',
    'admin.addUser': 'Agregar Usuario',
    'admin.editUser': 'Editar Usuario',
    'admin.deleteUser': 'Eliminar Usuario',
    'admin.userManagement': 'Gestión de Usuarios',
    'admin.role': 'Rol',
    'admin.permissions': 'Permisos',
    'admin.createdAt': 'Creado En',
    'admin.lastLogin': 'Último Acceso',
    'admin.actions': 'Acciones',
    'admin.admin': 'Administrador',
    'admin.user': 'Usuario',
    'admin.save': 'Guardar',
    'admin.cancel': 'Cancelar',
    'admin.delete': 'Eliminar',
    'admin.confirmDelete': '¿Estás seguro de que quieres eliminar este usuario?',
    'admin.userCreated': 'Usuario creado exitosamente',
    'admin.userUpdated': 'Usuario actualizado exitosamente',
    'admin.userDeleted': 'Usuario eliminado exitosamente',
    'admin.error': 'Ocurrió un error',
    'admin.attachReportPdf': 'Adjuntar informe (PDF)',
    'admin.uploadReport': 'Subir informe',
    'admin.uploadReportFailed': 'No se pudo subir el informe',
    'admin.orgDeletion.title': 'Programar eliminación de la organización',
    'admin.orgDeletion.warnTitle': 'Esta acción programa la eliminación permanente',
    'admin.orgDeletion.warnBody': 'La organización se conservará 5 días y luego se eliminará de forma permanente. Los usuarios y activos vinculados pueden perder acceso.',
    'admin.orgDeletion.organizationLabel': 'Organización',
    'admin.orgDeletion.confirmNameLabel': 'Escriba el nombre de la organización para confirmar',
    'admin.orgDeletion.confirmNameHint': 'Introduzca el nombre exactamente como aparece arriba (mayúsculas y espacios importan).',
    'admin.orgDeletion.nameMismatchInline': 'El nombre no coincide. Se esperaba: {{name}}',
    'admin.orgDeletion.nameMismatch': 'El nombre introducido no coincide con el de la organización. Cópielo exactamente, incluidas mayúsculas y espacios.',
    'admin.orgDeletion.commentLabel': 'Motivo de la eliminación (auditoría)',
    'admin.orgDeletion.commentPlaceholder': 'Describa por qué debe eliminarse esta organización…',
    'admin.orgDeletion.commentHint': 'Este comentario es obligatorio y se guardará en el registro de auditoría.',
    'admin.orgDeletion.commentTooShortInline': 'Se requieren al menos 5 caracteres.',
    'admin.orgDeletion.commentTooShort': 'Indique un motivo de al menos 5 caracteres. Se guardará en el registro de auditoría.',
    'admin.orgDeletion.alreadyScheduled': 'La eliminación de esta organización ya está programada.',
    'admin.orgDeletion.notFound': 'No se encontró la organización. Actualice la página e inténtelo de nuevo.',
    'admin.orgDeletion.genericError': 'No se pudo programar la eliminación de la organización.',
    'admin.orgDeletion.submit': 'Programar eliminación',
    'admin.orgDeletion.submitting': 'Programando…',
    'admin.orgDeletion.success': 'Eliminación programada para {{name}}.',
    'admin.orgDeletion.successWithDate': 'Eliminación programada para {{name}}. Eliminación permanente prevista: {{date}}.',
    
    // Permissions
    'permissions.all': 'Acceso Total',
    'permissions.dashboard': 'Acceso al Panel',
    'permissions.assets': 'Acceso a Activos',
    'permissions.compliance': 'Acceso a Cumplimiento',
    'permissions.customerTrust': 'Acceso a Gobernanza y Documentación de Seguridad',
    'permissions.suppliers': 'Acceso a Proveedores',
    'permissions.reports': 'Acceso a Informes',
    'permissions.integrations': 'Acceso a Integraciones',
    // incidents/alerts removed
    
    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.save': 'Guardar',
    'common.saving': 'Guardando…',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.add': 'Agregar',
    'common.close': 'Cerrar',
    'common.yes': 'Sí',
    'common.no': 'No',
    'common.select': 'Seleccionar',
    'common.saved': 'Guardado',

    // Starter Guide
    'starter.loading': 'Cargando guía de inicio...',
    'starter.header.title': 'Complete este formulario para una evaluación rápida de su infraestructura digital',
    'starter.header.p1': 'Analizaremos riesgos y daremos feedback rápido — sin conocimientos técnicos.',
    'starter.header.p2': 'Rápido y confidencial.',
    'starter.form.sector': 'Sector*',
    'starter.form.hasIS': '¿Opera sistemas de información?*',
    'starter.form.location': '¿Dónde están ubicados los sistemas?',
    'starter.form.processEU': '¿Procesa datos personales de ciudadanos de la UE?',
    'starter.form.dataTypes': 'Tipos de datos',
    'starter.form.strategy': 'Estrategia de seguridad de la información',
    'starter.form.responsible': 'Responsable de riesgos de seguridad',
    'starter.form.team': 'Equipo/Responsable de ciberseguridad',
    'starter.form.drp': 'Plan de recuperación/incidentes',
    'starter.form.vulnTest': 'Pruebas de vulnerabilidades',
    'starter.form.wantReport': '¿Recibir informe breve para sistemas públicos?',
    'starter.form.publicResources': 'Recursos públicos (dominios/IP)',
    'starter.form.standards': 'Estándares relevantes',
    'starter.form.certAudit': 'Auditoría certificada en 12 meses',
    'starter.form.preAudit': 'Interesado en pre-auditoría',
    'starter.form.selfAssess': 'Autoevaluación en la plataforma',
    'starter.form.name': 'Nombre*',
    'starter.form.companyName': 'Empresa*',
    'starter.form.email': 'Email*',
    'starter.form.phone': 'Teléfono*',
    'starter.form.privacy': 'Política de privacidad aceptada',
    'starter.loadFailed': 'No se pudo cargar la guía de inicio',
    'starter.validation.failed': 'La validación falló',
    'starter.validation.required': 'Obligatorio',
    'starter.validation.invalid_format': 'Formato no válido',
    'starter.validation.invalid_enum': 'Elija un valor de la lista',
    'starter.field.sector': 'Sector',
    'starter.field.contactName': 'Nombre',
    'starter.field.companyName': 'Empresa',
    'starter.field.email': 'Email',
    'starter.field.phone': 'Teléfono',
    'starter.field.privacyPolicyAccepted': 'Política de privacidad',
    'starter.field.systemLocation': 'Ubicación de los sistemas',
    'starter.field.hasSecurityStrategy': 'Estrategia de seguridad',
    'starter.field.hasCybersecurityTeam': 'Equipo de ciberseguridad',
    'starter.field.hasDisasterRecoveryPlan': 'Plan de recuperación',
    'starter.field.testsVulnerabilities': 'Pruebas de vulnerabilidades',
    'starter.field.plansCertifiedAudit': 'Auditoría certificada',
    'starter.btn.save': 'Guardar',
    'starter.btn.saving': 'Guardando...',
    'starter.summary.title': 'Resumen',
    'starter.summary.company': 'Empresa',
    'starter.summary.user': 'Usuario',
    'starter.summary.email': 'Email',
    'starter.summary.phone': 'Teléfono',
    'starter.summary.regSystems': 'Sistemas registrados',
    'starter.summary.regSystemsHelper': 'Ingrese dominios/IP separados por coma o espacio',
    'starter.summary.recommended': 'Estándares recomendados',
    'starter.btn.editForm': 'Editar formulario',
    'starter.btn.exportPdf': 'Exportar PDF',
    'starter.btn.exportExcel': 'Exportar Excel',

    // User Settings
    'user.editProfile': 'Editar perfil',
    'user.profileUpdated': 'Perfil actualizado con éxito',
    'user.profileNotFound': 'Perfil no encontrado',
    'user.unableLoad': 'No se pudo cargar el perfil del usuario.',
    'user.profileInfo': 'Información del perfil',
    'user.usernameImmutable': 'El nombre de usuario no se puede cambiar',
    'user.currentPassword': 'Contraseña actual',
    'user.newPasswordOptional': 'Nueva contraseña (opcional)',
    'user.confirmNewPassword': 'Confirmar nueva contraseña',
    'user.saveChanges': 'Guardar cambios',
    'user.accountDetails': 'Detalles de la cuenta',
    'user.memberSince': 'Miembro desde',
    'user.lastLogin': 'Último acceso',
    'user.permissions': 'Permisos',
    'user.yourPermissions': 'Tus permisos',
    'user.updateProfileFailed': 'No se pudo actualizar el perfil',
    'user.changePasswordFailed': 'No se pudo cambiar la contraseña',
    'auth.passwordsDontMatch': 'Las contraseñas no coinciden',

  }
}

const localeBundles: Record<Language, Translations> = {
  en: flattenLocale(enLocale as Record<string, unknown>),
  es: flattenLocale(esLocale as Record<string, unknown>),
  ru: flattenLocale(ruLocale as Record<string, unknown>),
}

Object.entries(localeBundles).forEach(([language, bundle]) => {
  translations[language] = { ...(translations[language] || {}), ...bundle }
})

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language') as Language
    if (saved) return saved
    const browser = String(globalThis?.navigator?.language || 'en').toLowerCase()
    if (browser.startsWith('ru')) return 'ru'
    if (browser.startsWith('es')) return 'es'
    return 'en'
  })

  const t = (key: string, fallback?: string, params?: Record<string, string | number>): string => {
    const currentLocale = translations[language] || {}
    const valueFromLocale = currentLocale[key]
    const fallbackFromDefault = translations.en?.[key]
    let result = valueFromLocale ?? fallbackFromDefault ?? fallback ?? key

    if (params && typeof result === 'string') {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        const stringValue = String(paramValue)
        const bracePattern = new RegExp(`\\{\\s*${paramKey}\\s*\\}`, 'g')
        const doubleBracePattern = new RegExp(`\\{\\{\\s*${paramKey}\\s*\\}\\}`, 'g')
        result = result.replace(doubleBracePattern, stringValue).replace(bracePattern, stringValue)
      })
    }

    return result
  }

  const allowedLanguages = useMemo<Language[]>(() => {
    return ['en', 'es', 'ru'] as Language[]
  }, [])

  // Enforce restriction on initial mount
  useEffect(() => {
    if (!allowedLanguages.includes(language)) {
      const fallback: Language = allowedLanguages[0] || 'en'
      setLanguage(fallback)
      localStorage.setItem('language', fallback)
    }
  }, [allowedLanguages, language])

  const handleSetLanguage = (lang: Language) => {
    if (!allowedLanguages.includes(lang)) return
    setLanguage(lang)
    localStorage.setItem('language', lang)
  }

  return (
    <I18nContext.Provider value={{ language, setLanguage: handleSetLanguage, t, allowedLanguages }}>
      {children}
    </I18nContext.Provider>
  )
}

export const useI18n = () => {
  const context = useContext(I18nContext)
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}